import type { Editor } from '@tiptap/core'
import { create, type StoreApi, type UseBoundStore } from 'zustand'
import type {
  DocumentId,
  DocumentResourceType,
  EditorThread,
} from '../CollaborationTextEditor/types'

export type TiptapEditorResource = {
  id: string
  type: DocumentResourceType
  name?: string
}

export type RegisteredTiptapEditor = {
  documentId: DocumentId
  editor: Editor
  resource: TiptapEditorResource | null
  readThreads: () => EditorThread[]
  /**
   * Whether this is the editor the user last focused. Clicking into the Gem chat
   * input blurs every editor, so "focused right now" is always false by the time
   * a message is sent; the last one focused is where the user was working.
   */
  isLastFocused: () => boolean
}

export type TiptapEditorRegistry = {
  editors: Map<DocumentId, RegisteredTiptapEditor>
  lastFocusedDocumentId: DocumentId | null
  registerEditor(entry: RegisteredTiptapEditor): void
  unregisterEditor(documentId: DocumentId): void
  focusEditor(documentId: DocumentId): void
}

export type TiptapEditorRegistryStore = UseBoundStore<
  StoreApi<TiptapEditorRegistry>
>

export const createTiptapEditorRegistryStore = (): TiptapEditorRegistryStore =>
  create<TiptapEditorRegistry>()((set) => ({
    editors: new Map(),
    lastFocusedDocumentId: null,

    registerEditor(entry) {
      set((state) => {
        const editors = new Map(state.editors)
        editors.set(entry.documentId, entry)
        return { editors }
      })
    },

    unregisterEditor(documentId) {
      set((state) => {
        const editors = new Map(state.editors)
        editors.delete(documentId)
        return { editors }
      })
    },

    // Deliberately not cleared on unregister: a view-mode switch re-registers
    // the same document, and it must stay the focused one. An id whose editor
    // is gone simply matches no entry.
    focusEditor(documentId) {
      set({ lastFocusedDocumentId: documentId })
    },
  }))
